import { z } from 'zod';

import {
  NotificationDeliveryListItemApiSchema,
  toNotificationDeliveryListItem,
} from './get-notification-deliveries.schema';

// ── Content snapshot (shape follows channel) ────────────
const EmailContentApiSchema = z
  .object({ subject: z.string(), html: z.string() })
  .transform(raw => ({
    type: 'email' as const,
    subject: raw.subject,
    html: raw.html,
  }));

const WhatsappContentApiSchema = z
  .object({ text: z.string() })
  .transform(raw => ({ type: 'whatsapp' as const, text: raw.text }));

const InAppContentApiSchema = z
  .object({
    title: z.string().nullable(),
    body: z.string().nullable(),
    action_url: z.string().nullable(),
  })
  .transform(raw => ({
    type: 'in_app' as const,
    title: raw.title,
    body: raw.body,
    actionUrl: raw.action_url,
  }));

const ContentApiSchema = z
  .union([
    EmailContentApiSchema,
    WhatsappContentApiSchema,
    InAppContentApiSchema,
  ])
  .nullable();

export type NotificationDeliveryContentDTO = z.infer<typeof ContentApiSchema>;

// ── Delivery Attempt ────────────────────────────────────
const DeliveryAttemptApiSchema = z
  .object({
    id: z.string(),
    trigger: z.string(),
    triggered_by_admin: z
      .object({ id: z.string(), name: z.string().nullable() })
      .nullable(),
    address: z.string().nullable(),
    result: z.string(),
    error: z.string().nullable(),
    started_at: z.string(),
    finished_at: z.string(),
  })
  .transform(raw => ({
    id: raw.id,
    trigger: raw.trigger,
    triggeredByAdmin: raw.triggered_by_admin,
    address: raw.address,
    result: raw.result,
    error: raw.error,
    startedAt: raw.started_at,
    finishedAt: raw.finished_at,
  }));

export type DeliveryAttemptDTO = z.infer<typeof DeliveryAttemptApiSchema>;

// ── Notification Delivery detail → DTO ──────────────────
const NotificationDeliveryDetailApiSchema =
  NotificationDeliveryListItemApiSchema.extend({
    last_address: z.string().nullable(),
    last_error: z.string().nullable(),
    content: ContentApiSchema,
    attempts: z.array(DeliveryAttemptApiSchema),
  }).transform(raw => ({
    ...toNotificationDeliveryListItem(raw),
    lastAddress: raw.last_address,
    lastError: raw.last_error,
    content: raw.content,
    attempts: raw.attempts,
  }));

export type NotificationDeliveryDetailDTO = z.infer<
  typeof NotificationDeliveryDetailApiSchema
>;

const GetNotificationDeliveryApiSchema = z
  .object({ result: NotificationDeliveryDetailApiSchema })
  .transform(raw => raw.result);

export function toNotificationDeliveryDetailDTO(
  raw: unknown
): NotificationDeliveryDetailDTO {
  return GetNotificationDeliveryApiSchema.parse(raw);
}
